import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@mui/material";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../layout/PageTitle";
import { createEntityStore, PropValidator } from "../../common/entityStore";
import { boolInputMarginTop, entityHeader, propBoolField, propEnumField, propNumberField, propSimpleEnumField, propTextField } from "../../common/entityStoreForm";
import { useEffect, useState } from "react";
import { CustomerStateId, CustomerFlagId, CustomerFlagType, EnumItemDto, RetentionCodeId, useCustomerSexTypes, useCustomerStates, useCustomerFlags, useOrderStates } from "../../common/enum";
import { AddressInputProps, useAddressSuggestions } from "../../common/addressSuggestions";
import { UserTypeId, useAccountStore } from "../../common/account";
import { useDefaultValues } from "../../common/defaultValues";
import { StoreWrapper } from "../../common/store";
import { ArDialog, ArListDto, customerArButtonTitle } from "./ArDialog";
import { AccountBalanceWallet, CurrencyExchange, Euro, History, Home, Replay, Print, Delete, DeleteForever, Subscriptions } from "@mui/icons-material";
import { useDialog, useSimpleDialog, yesNoButtons } from "../../common/dialog";
import { useCustomerDuplicityCheck } from "./duplicityCheck";
import { CustomerDto, CustomerOrderDto, newCustomer, newCustomerPathFragment, OrderListItem, OrderRetentionSaveDto } from "./types";
import { getCustomerAddressBaseValidators } from "./validators";
import { CustomerHistoryDialog } from "./CustomerHistoryDialog";
import { AddressHistoryDialog } from "./AddressHistoryDialog";
import { OrderList } from "./OrderList";
import { getLanguages, propCapitalizeFirstLetter, useRxJsSub, withSameParam } from "../../common/utils";
import { PaymentListDialog } from "./PaymentListDialog";
import { ReturnListDialog } from "./ReturnListDialog";
import { PrintDialog } from "./PrintDialog";
import { ContactList } from "./ContactList";
import { getOrderState, useCustomerBasicInfo } from "./common";
import { createListStore } from "../../common/listStore";
import { combineLatest, map } from "rxjs";
import { RefundListDialog } from "./RefundListDialog";
import { BgColorNegative } from "../../common/form";
import { createAxios } from "../../common/axios";
import { CustomerAddressList } from "./CustomerAddressList";
import { CustomerEPreferenceDialog } from "./CustomerEPreferenceDialog";
import { useDeliveryMethods } from "../../common/enum";
import { propRequired } from "../../common/entityStoreValidators";

export const CustomerPage = withSameParam("custId", changingCustId => () => {
    const navigate = useNavigate();
    const initialCustId = useParams().custId;
    const initialIsNewCustomer = initialCustId === newCustomerPathFragment;
    const [useCustomerStore] = useState(() => createEntityStore<CustomerDto>({
        path: "customers/customers",
        id: initialIsNewCustomer ? null : initialCustId,
        getNewId: p => p.custId,
        data: newCustomer,
        saveProps: ["title", "fname", "lname", "strt1", "strt2", "coadr", "zipCode", "city", "mob", "tel", "email", "customerSexTypeId", "customerStateId",
            "allowOfr", "langPref", "retention",
            "emailStopId", "mailStopId", "phoneStopId",
            "emailStatusId", "mailStatusId", "telStatusId", "mobStatusId",
            "emailStopSystemId", "loserStatusId",
        ],
        onCreated(p) {
            changingCustId(p.custId);
            navigate("/customers/" + p.custId, { replace: true });
        }, onSaved(data) {
            useCustomerStore.getState().setClientDataProp("retention", null);
            if ((useCustomerStore.getState().clientData.langPref || "") === "") {
                useCustomerStore.getState().setClientDataProp("langPref", data.langPref);
            }
        },
        checkDataBeforeSave: async (entity, entityId) => await customerDuplicityCheck.check(entity, entityId),
    }));
    useRxJsSub(() => useCustomerStore.getState().serverData$.subscribe({
        next(value) {
            useCustomerStore.getState().setClientDataProp("dunLvl", value.data.dunLvl);
            useCustomerStore.getState().setClientDataProp("sc1", value.data.sc1);
            useCustomerStore.getState().setClientDataProp("sc2", value.data.sc2);
            const wasDeleted = !useCustomerStore.getState().clientData.isDeleted && value.data.isDeleted;
            if (wasDeleted) {
                useCustomerStore.getState().setClientDataProp("customerStateId", value.data.customerStateId);
            }
            useCustomerStore.getState().setClientDataProp("isDeleted", value.data.isDeleted);
        }
    }));
    const customerStore = useCustomerStore(p => ({
        isSaveDisabled: p.isSaveDisabled, setPropValidators: p.setPropValidators, custId: p.entityId,
        notFound: p.notFound, customerId: p.serverData.id, isFetching: p.isFetching, isFetched: p.isFetched, serverData$: p.serverData$,
        failedToFetch: p.failedToFetch, serverData: p.serverData, customerStateId: p.clientData.customerStateId, isClientDataSame: p.isClientDataSame,
        isDeleted: p.clientData.isDeleted,
        isAddressChanged:
            p.clientData.customerSexTypeId !== p.serverData.customerSexTypeId ||
            p.clientData.title !== p.serverData.title ||
            p.clientData.fname !== p.serverData.fname ||
            p.clientData.lname !== p.serverData.lname ||
            p.clientData.strt1 !== p.serverData.strt1 ||
            p.clientData.strt2 !== p.serverData.strt2 ||
            p.clientData.coadr !== p.serverData.coadr ||
            p.clientData.zipCode !== p.serverData.zipCode ||
            p.clientData.city !== p.serverData.city,
        isEmailChanged: p.clientData.email !== p.serverData.email,
        isTelChanged: p.clientData.tel !== p.serverData.tel,
        isMobChanged: p.clientData.mob !== p.serverData.mob,
        mailStatusId: p.clientData.mailStatusId,
        emailStatusId: p.clientData.emailStatusId,
        telStatusId: p.clientData.telStatusId,
        mobStatusId: p.clientData.mobStatusId,
    }));
    const custId = customerStore.custId;
    const isNewCustomer = custId == null;

    const customerDuplicityCheck = useCustomerDuplicityCheck(saveCustomer);
    const deliveryMethods = useDeliveryMethods();
    const def = useDefaultValues(p => p.clientData);
    useEffect(() => {
        const validators: (PropValidator<CustomerDto> | null | false)[] =
            getCustomerAddressBaseValidators(def.landCode, false, deliveryMethods.items.find(d => d.isDefault && d.code === "DPD"));
        const languages = getLanguages(def);
        if (languages.length > 1) {
            validators.push(propRequired("langPref"));
        }
        customerStore.setPropValidators(validators);
    }, [customerStore, deliveryMethods, def]);

    const [useOrderListStore] = useState(() => createListStore<OrderListItem>({
        data: combineLatest([customerStore.serverData$, useOrderStates.getState().items$]).pipe(map(p =>
            ({ items: addOrderState(p[0].data.orders, p[1].items), isFetching: p[0].isFetching || p[1].isFetching }))),
        itemsPerPage: 5, listProps: [
            { column: "prodCde", label: "Product", width: 70 },
            { column: "projCde", label: "Project", width: 70 },
            { column: "ordId", label: "Ord", width: 70 },
            { column: "ordDte", label: "Enrol. date", width: 100, type: "date" },
            { column: "refId", label: "Refer", width: 70 },
            { column: "shpUn", label: "Qty", width: 70 },
            { column: "dunLvl", label: "Dun", width: 70 },
            { column: "ordTyp", label: "Type", width: 70 },
            { column: "orderState", label: "State", width: 180 },
        ]
    }));
    const selectedOrder = useOrderListStore(p => p.selectedItem);

    const address = useAddressSuggestions(useCustomerStore);
    const accountStore = useAccountStore(p => ({ userTypeId: p.data?.accessTokenDecoded.userTypeId }));
    const isAdmin = accountStore.userTypeId === UserTypeId.Admin;
    const isSupervisor = accountStore.userTypeId === UserTypeId.Supervisor;
    const isEditRole = accountStore.userTypeId !== UserTypeId.CollectionAgency && accountStore.userTypeId !== UserTypeId.View;
    const disabled = !isEditRole;
    const addressInputProps: AddressInputProps = { disabled };
    const languages = getLanguages(def);
    const canDelete = !isNewCustomer && !customerStore.isDeleted && (isAdmin || isSupervisor);
    const canUndelete = !isNewCustomer && customerStore.isDeleted && isAdmin;

    const shouldUnsetMailStatus = customerStore.isAddressChanged &&
        customerStore.mailStatusId === CustomerFlagId.MailStatus_WrongAddress;
    const shouldResetDeadStatus = customerStore.isAddressChanged &&
        customerStore.customerStateId === CustomerStateId.Dead;

    const shouldUnsetEmailStatus = customerStore.isEmailChanged && (
        customerStore.emailStatusId === CustomerFlagId.EmailStatus_WrongEmail
    );
    const shouldUnsetTelStatus = customerStore.isTelChanged && (
        customerStore.telStatusId === CustomerFlagId.TelStatus_WrongNumber ||
        customerStore.telStatusId === CustomerFlagId.TelStatus_DuplicateNumber
    );
    const shouldUnsetMobStatus = customerStore.isMobChanged && (
        customerStore.mobStatusId === CustomerFlagId.MobStatus_WrongNumber ||
        customerStore.mobStatusId === CustomerFlagId.MobStatus_DuplicateNumber
    );

    const customerTextField = propTextField(useCustomerStore, { disabled });
    const customerEnumField = propEnumField(useCustomerStore, { disabled });
    const customerSimpleEnumField = propSimpleEnumField(useCustomerStore, { disabled });
    const customerBoolField = propBoolField(useCustomerStore, { disabled });
    const customerNumberField = propNumberField(useCustomerStore, { disabled });

    const customerHistoriesDialog = useDialog(close => <AddressHistoryDialog close={close} useCustomerStore={useCustomerStore} />, "lg");
    const arDialog = useDialog(close => <StoreWrapper useStore={() => useCustomerBasicInfo(useCustomerStore)}
        render={customer => <ArDialog close={close} customer={customer} />} />);
    const paymentsDialog = useDialog(close => <PaymentListDialog close={close} useCustomerStore={useCustomerStore} />, "sm");
    const returnsDialog = useDialog(close => <ReturnListDialog close={close} useCustomerStore={useCustomerStore} />, "sm");
    const historiesDialog = useDialog(close => <CustomerHistoryDialog close={close} useCustomerStore={useCustomerStore} />, "lg");
    const ePreferencesDialog = useDialog(close => <CustomerEPreferenceDialog close={close} useCustomerStore={useCustomerStore} />, "sm");
    const refundsOrderId = selectedOrder != null ? selectedOrder.id : useOrderListStore.getState().items.at(0)?.id;
    const refundsDialog = useDialog(close => <RefundListDialog close={close} orderId={refundsOrderId!}
        useCustomerStore={useCustomerStore} />, "sm");

    const printDialog = useDialog(close => <PrintDialog close={close} useCustomerStore={useCustomerStore} useOrderListStore={useOrderListStore} />, "lg");

    useEffect(() => {
        if (!customerStore.isClientDataSame) {
            window.onbeforeunload = () => "You have unsaved changes. You can leave to discard your changes, or cancel to continue editing.";
        }
        return () => {
            window.onbeforeunload = null;
        };
    }, [customerStore.isClientDataSame]);

    const blocker = useBlocker(p => {
        const custId = useCustomerStore.getState().serverData.custId;
        return (custId == null || p.nextLocation.pathname !== "/customers/" + custId) && !customerStore.isClientDataSame && !customerDuplicityCheck.getLeavingPage();
    });

    async function onRetentionSelected(retention: OrderRetentionSaveDto) {
        if ([RetentionCodeId.AddressChange].includes(retention.retentionCodeId)) {
            useCustomerStore.getState().setClientDataProp("retention", retention);
            useCustomerStore.getState().setServerData({ ...useCustomerStore.getState().serverData, retention });
            return true;
        }
        return false;
    }

    const simpleDialog = useSimpleDialog();

    function deleteCustomer() {
        simpleDialog.show("Delete customer", "Are you sure you want to delete this customer?", undefined, yesNoButtons(async () => {
            try {
                const { data: ar } = await createAxios().get<ArListDto>(`customers/customerArs/${customerStore.customerId}`);
                if (ar.balance > 0) {
                    simpleDialog.show("Open balance", "Warning, client has an open balance, see A/R!\nReminders will be sent.");
                }

                await createAxios().post(`customers/customers/${custId}/delete`);
                if (!isAdmin) {
                    navigate("/customers");
                } else {
                    useCustomerStore.getState().fetchServerData();
                }
            } catch {
                useCustomerStore.getState().fetchServerData();
            }
        }));
    }
    function undeleteCustomer() {
        simpleDialog.show("Undelete customer", "Are you sure you want to undelete this customer?", undefined, yesNoButtons(async () => {
            try {
                await createAxios().post(`customers/customers/${custId}/undelete`);
            } finally {
                useCustomerStore.getState().fetchServerData();
            }
        }));
    }

    if (customerStore.notFound) {
        return <PageTitle title={`Customer ${custId} not found`} />;
    }

    if (customerStore.failedToFetch) {
        return <PageTitle title={`Customer ${custId} failed to load`} />;
    }

    async function saveCustomer() {
        useCustomerStore.getState().enableValidation();
        if (useCustomerStore.getState().isSaveDisabled) {
            return;
        }

        if (shouldUnsetMailStatus) {
            await new Promise<void>(resolve => {
                const mailStatusName = useCustomerFlags.getState().items.find(p => p.id === customerStore.mailStatusId)?.name;
                simpleDialog.show("Mail status", `Customer has "${mailStatusName}" mail status and address is being updated.\nDo you want to clear the "${mailStatusName}" mail status?`, undefined, yesNoButtons(async () => {
                    useCustomerStore.getState().setClientDataProp("mailStatusId", CustomerFlagId.MailStatus_None);
                    resolve();
                }, () => {
                    resolve();
                }));
            });
        }

        if (shouldResetDeadStatus) {
            await new Promise<void>(resolve => {
                const statusName = useCustomerStates.getState().items.find(p => p.id === customerStore.customerStateId)?.name;
                const activeStatusName = useCustomerStates.getState().items.find(p => p.id === CustomerStateId.Active)?.name;
                simpleDialog.show("Dead status", `Customer has "${statusName}" status set and address is being updated.\nDo you want to set the "${activeStatusName}" status?`, undefined, yesNoButtons(async () => {
                    useCustomerStore.getState().setClientDataProp("customerStateId", CustomerStateId.Active);
                    resolve();
                }, () => {
                    resolve();
                }));
            });
        }

        if (shouldUnsetEmailStatus) {
            await new Promise<void>(resolve => {
                const emailStatusName = useCustomerFlags.getState().items.find(p => p.id === customerStore.emailStatusId)?.name;
                simpleDialog.show("E-mail status", `Customer has "${emailStatusName}" e-mail status and e-mail is being updated.\nDo you want to clear the "${emailStatusName}" e-mail status?`, undefined, yesNoButtons(async () => {
                    useCustomerStore.getState().setClientDataProp("emailStatusId", CustomerFlagId.EmailStatus_None);
                    resolve();
                }, () => {
                    resolve();
                }));
            });
        }

        if (shouldUnsetTelStatus) {
            await new Promise<void>(resolve => {
                const telStatusName = useCustomerFlags.getState().items.find(p => p.id === CustomerFlagId.TelStatus_WrongNumber)?.name;
                simpleDialog.show("Phone status", `Customer has "${telStatusName}" phone status and phone is being updated.\nDo you want to clear the "${telStatusName}" phone status?`, undefined, yesNoButtons(async () => {
                    useCustomerStore.getState().setClientDataProp("telStatusId", CustomerFlagId.TelStatus_None);
                    resolve();
                }, () => {
                    resolve();
                }));
            });
        }

        if (shouldUnsetMobStatus) {
            await new Promise<void>(resolve => {
                const mobStatusName = useCustomerFlags.getState().items.find(p => p.id === CustomerFlagId.MobStatus_WrongNumber)?.name;
                simpleDialog.show("Phone status", `Customer has "${mobStatusName}" mobile status and phone is being updated.\nDo you want to clear the "${mobStatusName}" mobile status?`, undefined, yesNoButtons(async () => {
                    useCustomerStore.getState().setClientDataProp("mobStatusId", CustomerFlagId.MobStatus_None);
                    resolve();
                }, () => {
                    resolve();
                }));
            });
        }

        useCustomerStore.getState().save();
    }

    return <>
        {customerDuplicityCheck.render()}
        {customerHistoriesDialog.render()}
        {arDialog.render()}
        {paymentsDialog.render()}
        {returnsDialog.render()}
        {historiesDialog.render()}
        {refundsDialog.render()}
        {ePreferencesDialog.render()}
        {printDialog.render()}
        {simpleDialog.render()}

        <Dialog open={blocker.state === "blocked"} onClose={blocker.reset}>
            <Box m={2}>
                <DialogTitle>Are you sure you want to leave the page?</DialogTitle>
                <DialogContent>You can leave to discard your changes, or cancel to continue editing.</DialogContent>
                <DialogActions>
                    <Button onClick={blocker.reset} color="positive">Cancel</Button>
                    <Button onClick={blocker.proceed} color="negative">Leave</Button>
                </DialogActions>
            </Box>
        </Dialog>

        <PageTitle title={isNewCustomer ? "New customer" : `Customer ${custId} - ${customerStore.serverData.fname || ""} ${customerStore.serverData.lname || ""}` +
            (customerStore.isDeleted ? " (deleted)" : "")} />

        <Grid container columnSpacing={4} alignItems="stretch">
            <Grid item container columnSpacing={2} xs={6} alignSelf="flex-start">

                {entityHeader([
                    canDelete && ["delete", () => <Button title="Disable customer" disabled={!canDelete || customerStore.isFetching} color="negative" variant="outlined"
                        onClick={deleteCustomer}><Delete /></Button>],
                    canUndelete && ["undelete", () => <Button title="Enable customer" disabled={!canUndelete || customerStore.isFetching}
                        onClick={undeleteCustomer}><DeleteForever /></Button>],
                    ["customerHistories", () => <Button title="Address history" disabled={customerStore.customerId == null}
                        onClick={() => customerHistoriesDialog.show()}><Home /></Button>],
                    ["returns", () => <Button title="Returns" disabled={customerStore.customerId == null}
                        onClick={() => returnsDialog.show()}><Replay /></Button>],
                    ["payments", () => <Button title="Payments" disabled={customerStore.customerId == null}
                        onClick={() => paymentsDialog.show()}><Euro /></Button>],
                    ["refunds", () => <Button disabled={refundsOrderId == null} title="Refunds"
                        onClick={() => refundsDialog.show()}><CurrencyExchange /></Button>],
                    ["ar", () => <Button title={customerArButtonTitle} disabled={customerStore.customerId == null}
                        onClick={() => arDialog.show()}><AccountBalanceWallet /></Button>],
                    ["histories", () => <Button title="Customer history" disabled={customerStore.customerId == null}
                        onClick={() => historiesDialog.show()}><History /></Button>],
                    ["ePreferences", () => <Button title="Email preferences" disabled={customerStore.customerId == null}
                        onClick={() => ePreferencesDialog.show()}><Subscriptions /></Button>],
                    ["print", () => <Button title="Print letter" disabled={customerStore.customerId == null || !isEditRole}
                        onClick={() => printDialog.show()}><Print /></Button>],
                ], [
                    ["save", () => <Button style={{ paddingLeft: 20, paddingRight: 20 }} disabled={customerStore.isSaveDisabled} onClick={() => saveCustomer()} color="save">Save</Button>]
                ])}

                <Grid item xs={4}><TextField value={custId || ""} label="Cust ID" disabled /></Grid>
                <Grid item xs={4}>{customerEnumField("customerSexTypeId", "Gender", useCustomerSexTypes)}</Grid>
                <Grid item xs={4}>{languages.length > 1 && customerSimpleEnumField("langPref", "Language", languages)}</Grid>

                <Grid item xs={4}>{customerTextField("title", "Title")}</Grid>
                <Grid item xs={4}>{customerTextField("fname", "First name", { onBlur: () => propCapitalizeFirstLetter("fname", useCustomerStore) })}</Grid>
                <Grid item xs={4}>{customerTextField("lname", "Last name", { onBlur: () => propCapitalizeFirstLetter("lname", useCustomerStore) })}</Grid>

                <Grid item xs={12}>{customerTextField("coadr", "C/O Address")}</Grid>

                <Grid item xs={8}>{address.renderStreetInput(addressInputProps)}</Grid>
                <Grid item xs={4}>{customerTextField("strt2", "House number")}</Grid>

                <Grid item xs={4}>{address.renderZipInput(addressInputProps)}</Grid>
                <Grid item xs={8}>{address.renderCityInput(addressInputProps)}</Grid>

                <Grid item xs={4}>{customerTextField("tel", "Phone", {
                    onChange: v => {
                        if ((v ?? "").trim() === "" && [CustomerFlagId.TelStatus_WrongNumber, CustomerFlagId.TelStatus_DuplicateNumber]
                            .includes(useCustomerStore.getState().clientData.telStatusId)) {
                            useCustomerStore.getState().setClientDataProp("telStatusId", CustomerFlagId.TelStatus_None);
                        }
                    }
                })}</Grid>
                <Grid item xs={4}>{customerTextField("mob", "Mobile", {
                    onChange: v => {
                        if ((v ?? "").trim() === "" && [CustomerFlagId.MobStatus_WrongNumber, CustomerFlagId.MobStatus_DuplicateNumber]
                            .includes(useCustomerStore.getState().clientData.mobStatusId)) {
                            useCustomerStore.getState().setClientDataProp("mobStatusId", CustomerFlagId.MobStatus_None);
                        }
                    }
                })}</Grid>
                <Grid item xs={4} />

                <Grid item xs={12}>{customerTextField("email", "E-mail")}</Grid>

                <Grid item xs={3}>{customerEnumField("customerStateId", "Status", useCustomerStates, {
                    bgColor: customerStore.customerStateId !== CustomerStateId.Active ? BgColorNegative : undefined,
                    disabled: customerStore.isDeleted,
                })}</Grid>
                <Grid item xs={3} mt={boolInputMarginTop}>{customerBoolField("allowOfr", "Allow offers")}</Grid>
                <Grid item xs={2}>{customerNumberField("dunLvl", "dunLvlFormatted", "Dun level", { disabled: true })}</Grid>
                <Grid item xs={2}>{customerTextField("sc1", "SC1", { disabled: true })}</Grid>
                <Grid item xs={2}>{customerTextField("sc2", "SC2", { disabled: true })}</Grid>

                <Grid item xs={4}>{customerEnumField("mailStopId", "Mail stop", useCustomerFlags, {
                    noneValue: CustomerFlagId.MailStop_No,
                    filter: p => p.type === CustomerFlagType.MailStop,
                })}</Grid>
                <Grid item xs={4}>{customerEnumField("emailStopId", "E-mail stop", useCustomerFlags, {
                    noneValue: CustomerFlagId.EmailStop_No,
                    filter: p => p.type === CustomerFlagType.EmailStop,
                })}</Grid>
                <Grid item xs={4}>{customerEnumField("phoneStopId", "Phone stop", useCustomerFlags, {
                    noneValue: CustomerFlagId.PhoneStop_No,
                    filter: p => p.type === CustomerFlagType.PhoneStop,
                })}</Grid>
                <Grid item xs={4}>{customerEnumField("mailStatusId", "Mail status", useCustomerFlags, {
                    noneValue: CustomerFlagId.MailStatus_None,
                    filter: p => p.type === CustomerFlagType.MailStatus,
                    bgColor: shouldUnsetMailStatus ? BgColorNegative : undefined,
                })}</Grid>
                <Grid item xs={4}>{customerEnumField("emailStatusId", "E-mail status", useCustomerFlags, {
                    noneValue: CustomerFlagId.EmailStatus_None,
                    filter: p => p.type === CustomerFlagType.EmailStatus,
                    bgColor: shouldUnsetEmailStatus ? BgColorNegative : undefined,
                })}</Grid>
                <Grid item xs={4}>{customerEnumField("telStatusId", "Phone status", useCustomerFlags, {
                    noneValue: CustomerFlagId.TelStatus_None,
                    filter: p => p.type === CustomerFlagType.TelStatus,
                    bgColor: shouldUnsetTelStatus ? BgColorNegative : undefined,
                })}</Grid>
                <Grid item xs={4}>{customerEnumField("mobStatusId", "Mobile status", useCustomerFlags, {
                    noneValue: CustomerFlagId.MobStatus_None,
                    filter: p => p.type === CustomerFlagType.MobStatus,
                    bgColor: shouldUnsetMobStatus ? BgColorNegative : undefined,
                })}</Grid>
                {isAdmin && <Grid item xs={4}>{customerEnumField("emailStopSystemId", "E-mail stop system", useCustomerFlags, {
                    noneValue: CustomerFlagId.EmailStopSystem_None,
                    filter: p => p.type === CustomerFlagType.EmailStopSystem,
                })}</Grid>}
                {isAdmin && <Grid item xs={4}>{customerEnumField("loserStatusId", "Loser status", useCustomerFlags, {
                    noneValue: CustomerFlagId.LoserStatus_None,
                    filter: p => p.type === CustomerFlagType.LoserStatus,
                })}</Grid>}

                <Grid item xs={12} mt={1}>
                    <CustomerAddressList useCustomerStore={useCustomerStore} disabled={disabled} />
                </Grid>
            </Grid>
            <Grid item container xs={6} rowSpacing={4} alignSelf="flex-start">
                <Grid item xs={12}><OrderList useCustomerStore={useCustomerStore} useOrderListStore={useOrderListStore} onRetentionSelected={onRetentionSelected} /></Grid>
                <Grid item xs={12}><ContactList useCustomerStore={useCustomerStore} /></Grid>
            </Grid>
        </Grid>
    </>;
});

function addOrderState(orders: CustomerOrderDto[], states: EnumItemDto[]) {
    return orders.map(p => ({
        ...p,
        orderState: getOrderState(p, states),
    }));
}
